import { useState, useEffect, useRef } from 'react';
import {
  aiGenerateSVG,
  arrowRightThinSVG,
  pauseSVG,
} from '../../../lib/allCustomSVGs';
import '../../../styles/pages/tara/taraUI/taraInput.scss';
import CustomSVGs from '../../../ui/customSVGs/CustomSVGs';
import { useTranslation } from 'react-i18next';
import { CurrentChatUpdate } from '../../../interfaces/taraInterfaces';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import generateRandomString from '../../../lib/helper-functions/generateRandomString';
import {
  toggleShowDeepInsights,
  setSelectedOpenAiModel,
} from '../../../store/reducers/chatSlice';
import check_tara_can_chat_status_api_call from '../../../lib/api/tara/check-tara-can-chat-status-api-call';
import { AnimatePresence, motion } from 'framer-motion';
import Popup from '../../../ui/popup/Popup';
import NoData from '../../../ui/noData/NoData';
import { useNavigate } from 'react-router-dom';
import { APP_ENV } from '../../../lib/constants/central-api';
import {
  setIsChatAnimTriggered,
  setIsSidebarMinimized,
} from '../../../store/reducers/uiSlice';
import SystemInstructions from './SystemInstructions';

type Props = {
  updateChat: ({
    newChat,
    role,
    messageType,
    newChatId,
  }: CurrentChatUpdate) => void;
  cancelAPI: () => void;
  loading: 'pending' | 'success' | 'error' | 'null';
};

const TaraInput = ({ updateChat, cancelAPI, loading }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const navigate = useNavigate();

  const canChatStatus = useSelector((state: RootState) => state.chat.canChat);
  const shopSlug = useSelector(
    (state: RootState) => state.user.company.shop_slug
  );
  const isDarkMode = useSelector((state: RootState) => state.UI.theme.dark);
  const currentChat = useSelector((state: RootState) => state.chat.currentChat);
  const showDeepInsights = useSelector(
    (state: RootState) => state.chat.showDeepInsights
  );
  const selectedOpenAiModel = useSelector(
    (state: RootState) => state.chat.selectedOpenAiModel
  );
  const isSidebarDrawerOpen = useSelector(
    (state: RootState) => state.UI.isSidebarDrawerOpen
  );
  const isHelpSetupPopupOpen = useSelector(
    (state: RootState) => state.UI.isHelpSetupPopupOpen
  );
  const isTaraChatAnimationEnabled = useSelector(
    (state: RootState) => state.UI.isTaraChatAnimationEnabled
  );
  const isChatAnimTriggered = useSelector(
    (state: RootState) => state.UI.isChatAnimTriggered
  );
  const [isNoIntegrationsPopupOpen, setIsNoIntegrationsPopupOpen] =
    useState(false);
  const [isSystemInstructionsPopupOpen, setIsSystemInstructionsPopupOpen] =
    useState(false);

  const [isManuallyDisabled, setIsManuallyDisabled] = useState(false); // ✅ NEW STATE
  const hasCheckedStatusRef = useRef(false); // Prevent multiple API calls

  const availableModels = [
    'gpt-5.1-chat-latest',
    'gpt-5',
    'gpt-4',
    'gpt-4-turbo',
    'gpt-3.5-turbo',
    'gpt-4o',
    'gpt-4o-mini',
    'gemini-1.5-pro',
  ];

  const getModelDisplayName = (model: string) => {
    const modelMap: { [key: string]: string } = {
      'gpt-5.1-chat-latest': 'GPT-5.1',
      'gpt-5': 'GPT-5',
      'gpt-4': 'GPT-4',
      'gpt-4-turbo': 'GPT-4 Turbo',
      'gpt-3.5-turbo': 'GPT-3.5 Turbo',
      'gpt-4o': 'GPT-4o',
      'gpt-4o-mini': 'GPT-4o Mini',
      'gemini-1.5-pro': 'Gemini 1.5 Pro',
    };
    return modelMap[model] || model;
  };

  const isDisabled =
    isManuallyDisabled ||
    (!!currentChat?.chatMessages?.length &&
      currentChat.chatMessages[currentChat.chatMessages.length - 1]?.role ===
        'user');

  const openNoIntegrationsPopupModal = () => {
    // Explicitly set to true instead of toggling to prevent race conditions
    setIsNoIntegrationsPopupOpen(true);
    setIsManuallyDisabled(true);
  };

  const closeNoIntegrationsPopupModal = () => {
    setIsNoIntegrationsPopupOpen(false);
  };

  const checkTaraCanChatStatus = async () => {
    if (hasCheckedStatusRef.current) return; // Prevent multiple simultaneous calls
    hasCheckedStatusRef.current = true;
    try {
      await check_tara_can_chat_status_api_call(
        navigate,
        openNoIntegrationsPopupModal
      );
    } finally {
      // Reset after a delay to allow for retries if needed
      setTimeout(() => {
        hasCheckedStatusRef.current = false;
      }, 1000);
    }
  };

  useEffect(() => {
    if (!shopSlug || shopSlug === '') return;
    checkTaraCanChatStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopSlug]);

  // Auto‐resize but clamp to CSS max‐height (20rem)
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const scrollH = ta.scrollHeight;
    const maxH = parseFloat(
      window.getComputedStyle(ta).maxHeight.replace('px', '')
    );
    const newH = !isNaN(maxH) ? Math.min(scrollH, maxH) : scrollH;
    ta.style.height = `${newH}px`;
  }, [inputValue]);

  const handleChangeValue = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
  };

  const onSubmit = () => {
    if (inputValue.trim() && !isDisabled) {
      updateChat({
        newChatId: generateRandomString(10),
        newChat: inputValue.trim(),
        role: 'user',
        messageType: 'text',
      });

      setInputValue('');
      setIsManuallyDisabled(false);

      // ✅ trigger the animation flow
      if (isTaraChatAnimationEnabled) {
        dispatch(setIsSidebarMinimized(true));
        if (!isChatAnimTriggered) {
          dispatch(setIsChatAnimTriggered(true));
        }
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  const atomSVG = (
    <svg
      fill={`${
        showDeepInsights
          ? 'var(--main-btn-text-color)'
          : 'var(--main-text-color)'
      }`}
      viewBox='0 0 24 24'
      xmlns='http://www.w3.org/2000/svg'
    >
      <line
        x1={11.95}
        y1={12}
        x2={12.05}
        y2={12}
        style={{
          fill: 'none',
          stroke: `${
            showDeepInsights
              ? 'var(--main-btn-text-color)'
              : 'var(--main-text-color)'
          }`,
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
          strokeWidth: 2,
        }}
      />
      <path
        d='M14.93,14.93c-4.7,4.7-9.82,7.19-11.44,5.58s.88-6.74,5.58-11.44,9.82-7.19,11.44-5.58S19.63,10.23,14.93,14.93ZM3.49,3.49c-1.61,1.62.88,6.74,5.58,11.44s9.82,7.19,11.44,5.58-.88-6.74-5.58-11.44S5.11,1.88,3.49,3.49Z'
        style={{
          fill: 'none',
          stroke: `${
            showDeepInsights
              ? 'var(--main-btn-text-color)'
              : 'var(--main-text-color)'
          }`,
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
          strokeWidth: 2,
        }}
      />
    </svg>
  );

  const modelSVG = (
    <svg fill='none' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'>
      <path
        stroke='var(--main-text-color)'
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        d='M4 13h3.439a.991.991 0 0 1 .908.6 3.978 3.978 0 0 0 7.306 0 .99.99 0 0 1 .908-.6H20M4 13v6a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-6M4 13l2-9h12l2 9'
      />
    </svg>
  );

  const settingsSVG = (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='24'
      height='24'
      viewBox='0 0 24 24'
    >
      <path d='M24 14.187v-4.374c-2.148-.766-2.726-.802-3.027-1.529-.303-.729.083-1.169 1.059-3.223l-3.093-3.093c-2.026.963-2.488 1.364-3.224 1.059-.727-.302-.768-.889-1.527-3.027h-4.375c-.764 2.144-.8 2.725-1.529 3.027-.752.313-1.203-.1-3.223-1.059l-3.093 3.093c.977 2.055 1.362 2.493 1.059 3.224-.302.727-.881.764-3.027 1.528v4.375c2.139.76 2.725.8 3.027 1.528.304.734-.081 1.167-1.059 3.223l3.093 3.093c1.999-.95 2.47-1.373 3.223-1.059.728.302.764.88 1.529 3.027h4.374c.758-2.131.799-2.723 1.537-3.031.745-.308 1.186.099 3.215 1.062l3.093-3.093c-.975-2.05-1.362-2.492-1.059-3.223.3-.726.88-.763 3.027-1.528zm-4.875.764c-.577 1.394-.068 2.458.488 3.578l-1.084 1.084c-1.093-.543-2.161-1.076-3.573-.49-1.396.581-1.79 1.693-2.188 2.877h-1.534c-.398-1.185-.791-2.297-2.183-2.875-1.419-.588-2.507-.045-3.579.488l-1.083-1.084c.557-1.118 1.066-2.18.487-3.58-.579-1.391-1.691-1.784-2.876-2.182v-1.533c1.185-.398 2.297-.791 2.875-2.184.578-1.394.068-2.459-.488-3.579l1.084-1.084c1.082.538 2.162 1.077 3.58.488 1.392-.577 1.785-1.69 2.183-2.875h1.534c.398 1.185.792 2.297 2.184 2.875 1.419.588 2.506.045 3.579-.488l1.084 1.084c-.556 1.121-1.065 2.187-.488 3.58.577 1.391 1.689 1.784 2.875 2.183v1.534c-1.188.398-2.302.791-2.877 2.183zm-7.125-5.951c1.654 0 3 1.346 3 3s-1.346 3-3 3-3-1.346-3-3 1.346-3 3-3zm0-2c-2.762 0-5 2.238-5 5s2.238 5 5 5 5-2.238 5-5-2.238-5-5-5z' />
    </svg>
  );

  return (
    <div className='taraInput__container'>
      <Popup
        styles={{ backgroundColor: '#fff' }}
        setTrigger={closeNoIntegrationsPopupModal}
        trigger={isNoIntegrationsPopupOpen && !isHelpSetupPopupOpen}
      >
        <NoData
          title={t('NoConnectedIntegrationsText1')}
          subTitle={t('NoConnectedIntegrationsText2')}
          buttonTitle={t('ConnectIntegrations')}
          buttonAction={() => {
            closeNoIntegrationsPopupModal();
            navigate('/integrations');
          }}
        />
      </Popup>
      <AnimatePresence>
        {canChatStatus === false && (
          <motion.div
            className='chatBlurFilter'
            initial={{ opacity: 0, backdropFilter: 'blur(0px)' }}
            animate={{ opacity: 1, backdropFilter: 'blur(4px)' }}
            exit={{ opacity: 0, backdropFilter: 'blur(0px)' }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          />
        )}
      </AnimatePresence>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        data-is-input-empty={inputValue.trim() === '' ? 'true' : 'false'}
        className='taraInput'
        data-is-dark-mode={isDarkMode.toString()}
      >
        <textarea
          ref={textareaRef}
          autoFocus
          value={inputValue}
          disabled={isSidebarDrawerOpen}
          onChange={handleChangeValue}
          placeholder={`${t('AskTaraAQuestion')}...`}
          onKeyDown={handleKeyDown}
        />
        <div className='taraInput__icon'>
          <CustomSVGs svg={aiGenerateSVG} />
        </div>
        <div className='taraInput__parameters'>
          {/* <div
            onClick={() => dispatch(toggleShowGraphsOnly())}
            className='taraInput__parameters--btn'
            data-is-graph-only={showGraphsOnly ? 'true' : 'false'}
            title={showGraphsOnly ? t('TurnOffGraphs') : t('ShowGraphsOnly')}
          >
            <CustomSVGs svg={chartSVG} />
            <span>{t('GraphsOnly')}</span>
          </div> */}
          {APP_ENV === 'DEV' && (
            <div
              onClick={() => setIsSystemInstructionsPopupOpen(true)}
              className='taraInput__parameters--btn'
              title='System Instructions'
            >
              <CustomSVGs svg={settingsSVG} />
              <span>System Instructions</span>
            </div>
          )}
          <div
            onClick={() => dispatch(toggleShowDeepInsights())}
            className='taraInput__parameters--btn'
            data-is-graph-only={showDeepInsights ? 'true' : 'false'}
            title={
              showDeepInsights ? t('TurnOffDeepInsight') : t('GetDeepInsight')
            }
          >
            <CustomSVGs svg={atomSVG} />
            <span>{t('DeepInsight')}</span>
          </div>
          {APP_ENV === 'DEV' && ( //? TODO - not enabled in production yet
            <div className='taraInput__parameters--dropdown'>
              <div className='taraInput__parameters--select-wrapper'>
                <CustomSVGs svg={modelSVG} />
                <span>{getModelDisplayName(selectedOpenAiModel)}</span>
                <svg
                  className='taraInput__parameters--arrow'
                  viewBox='0 0 24 24'
                  fill='none'
                >
                  <path
                    d='M6 9L12 15L18 9'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
                <select
                  value={selectedOpenAiModel}
                  onChange={(e) =>
                    dispatch(setSelectedOpenAiModel(e.target.value))
                  }
                  className='taraInput__parameters--select'
                  title='Select OpenAI Model'
                >
                  {availableModels.map((model) => (
                    <option key={model} value={model}>
                      {getModelDisplayName(model)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {loading === 'pending' ? (
          <button
            type='button'
            className='taraInput__button'
            title={t('StopResponse')}
            onClick={cancelAPI}
            style={{ borderRadius: '50%', width: '3.3rem' }}
          >
            <CustomSVGs svg={pauseSVG} />
          </button>
        ) : (
          <button
            type='submit'
            className='taraInput__button'
            disabled={isDisabled}
          >
            <CustomSVGs svg={arrowRightThinSVG} />
          </button>
        )}
      </form>
      <span>{t('TaraCanMakeMistakes')}</span>
      {APP_ENV === 'DEV' && (
        <Popup
          styles={{
            backgroundColor: 'var(--input-background-color)',
            width: '50rem',
            maxWidth: '90vw',
          }}
          setTrigger={() => setIsSystemInstructionsPopupOpen(false)}
          trigger={isSystemInstructionsPopupOpen}
        >
          <SystemInstructions />
        </Popup>
      )}
    </div>
  );
};

export default TaraInput;
